import { createApi } from '@reduxjs/toolkit/query/react';
import {
  AudienceResponse,
  AudiencesResponse,
  PostAudience,
  AudienceCatalogsResponse,
  SegmentsListResponse,
  PostAudienceCatalog,
  AudienceCatalogsFilters,
  SegmentsFilterResponse,
  SegmentsFilters,
  SegmentResponse,
  TrackHostsResponse,
  TrackHostsActionsResponse,
  PostAudienceSegmentItem,
  ContactSourcesResponse,
  ContactSourceValuesResponse,
  IntegrationsResponse,
  IntegrationValuesResponse,
  ContactSourceMapItem,
  OrganizerIntegrationsResponse,
  IntegrationPlatformsResponse,
  CreateIntegrationRequest,
  OrganizerIntegrationItem,
  IntegrationMappingResponse,
  UpdateIntegrationRequest,
  HubspotListsResponse,
  HubspotListFieldsResponse,
  HubspotListPreviewQueuedResponse,
  HubspotListPreviewMappingItem,
  HubspotListEnableImportResponse,
  HubspotListImportsResponse,
  HubspotListConnectResponse,
  HubspotListImportItem,
} from './models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { Meta } from 'lib.shared/src/types';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';

const normalizeMappingItem = (item: { code?: string; name?: string; field?: string | null }) => ({
  code: item.code ?? '',
  name: item.name ?? '',
  field: item.field ?? null,
});

// const audiences = `organizer_audiences`;
// const audience_catalogs = `audience_catalogs`;
// const segments = `audience_segments`;

// const event_audience_catalog = `event_audience_catalog`;
// const events_setup_progress = `events_setup_progress`;

export const organizerAudiencesApi = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_AUDIENCES,
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    API_EVENT_TAGS.ORGANIZER_AUDIENCES,
    API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG,
    API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS,
    API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG,
    API_EVENT_TAGS.EVENTS_SETUP_PROGRESS,
    API_EVENT_TAGS.ORGANIZER_CONTACT_SOURCES,
    API_EVENT_TAGS.ORGANIZER_INTEGRATIONS,
  ],
  endpoints: (build) => ({
    getOrganizerSegments: build.query<SegmentsListResponse, SegmentsFilters>({
      // todo add filters
      query: ({ search, page, statuses, events, channels, type }) => {
        const params = new URLSearchParams();

        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');
        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const channelsQueryString = getQueryStringFromParams(channels, 'channels');
        const typeQueryString = getQueryStringFromParams(type, 'type');

        if (search) {
          params.append('filters[search]', `${search}`);
        }

        if (page) {
          params.append('page', `${page}`);
        }

        const filterQueryString = params.toString();

        const queryString = [
          statusesQueryString,
          eventsQueryString,
          channelsQueryString,
          typeQueryString,
          filterQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/segments${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    getOrganizerSegmentById: build.query<SegmentResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/segments/${id}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      transformResponse: (response: SegmentResponse): SegmentResponse => ({
        data: {
          ...response.data,
          source: response.data.source
            ? {
                ...response.data.source,
                map: response.data.source.map?.map((item) => ({
                  code: normalizeMappingItem(item).code,
                  name: normalizeMappingItem(item).name,
                  field: normalizeMappingItem(item).field ?? '',
                })),
              }
            : response.data.source,
          integration: response.data.integration
            ? {
                ...response.data.integration,
                mapping_data: response.data.integration.mapping_data?.contacts?.length
                  ? {
                      contacts: response.data.integration.mapping_data.contacts.map((item) => ({
                        code: normalizeMappingItem(item).code,
                        name: normalizeMappingItem(item).name,
                        field: normalizeMappingItem(item).field ?? '',
                      })),
                    }
                  : response.data.integration.mapping_data,
              }
            : response.data.integration,
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    postSegment: build.mutation<void, PostAudienceSegmentItem>({
      query: (data) => ({
        url: `/segments`,
        method: 'POST',
        body: data,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    patchSegmentById: build.mutation<
      AudienceResponse,
      { data: PostAudienceSegmentItem; id?: number | string }
    >({
      query: ({ data, id }) => ({
        url: `/segments/${id ? id.toString() : ''}`,
        method: 'PATCH',
        body: data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    getOrganizerAudiences: build.query<
      AudiencesResponse,
      {
        page?: number;
        statuses?: string[];
        events?: string[];
        segments?: string[];
        search?: string;
      }
    >({
      query: ({ search, page, statuses, events, segments }) => {
        const params = new URLSearchParams();

        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');
        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const segmentsQueryString = getQueryStringFromParams(segments, 'segments');

        if (search) {
          params.append('filters[search]', `${search}`);
        }

        // todo check page
        if (page) {
          params.append('page', `${page}`);
        }

        const filterQueryString = params.toString();

        const queryString = [
          statusesQueryString,
          eventsQueryString,
          segmentsQueryString,
          filterQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/organizer/audiences${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCES, API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    getOrganizerAudienceById: build.query<AudienceResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/organizer/audiences/${id}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCES],
    }),
    postAudience: build.mutation<AudienceResponse, PostAudience>({
      query: (data) => ({
        url: `/organizer/audiences`,
        method: 'POST',
        body: data,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_AUDIENCES },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    patchAudienceById: build.mutation<AudienceResponse, { data: PostAudience; id?: number }>({
      query: ({ data, id }) => ({
        url: `/organizer/audiences/${id ? id.toString() : ''}`,
        method: 'PATCH',
        body: data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: () => [{ type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS }],
    }),
    patchAudienceStatusById: build.mutation<
      AudienceResponse,
      { id?: number; statusId: number; transition: number }
    >({
      query: ({ id, statusId, transition }) => ({
        url: `/organizer/audiences/${id ? id.toString() : ''}/status/${statusId ? statusId.toString() : ''}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
        providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCES],
      }),
    }),

    getOrganizerAudienceCatalogs: build.query<AudienceCatalogsResponse, AudienceCatalogsFilters>({
      query: ({ search, page, statuses, audiences: audiencesParams, events, segments }) => {
        const params = new URLSearchParams();

        const statusesQueryString = getQueryStringFromParams(statuses, 'statuses');
        const audiencesQueryString = getQueryStringFromParams(audiencesParams, 'audiences');
        const eventsQueryString = getQueryStringFromParams(events, 'events');
        const segmentsQueryString = getQueryStringFromParams(segments, 'segments');

        if (search) {
          params.append('filters[search]', `${search}`);
        }
        // todo check page
        if (page) {
          params.append('page', `${page}`);
        }

        const filterQueryString = params.toString();

        const queryString = [
          statusesQueryString,
          filterQueryString,
          audiencesQueryString,
          eventsQueryString,
          segmentsQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `/organizer/audience-catalogs${queryString.length ? `/?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    getOrganizerAudienceCatalogById: build.query<AudienceResponse, { id: string }>({
      query: ({ id }) => {
        return {
          url: `/organizer/audience-catalogs/${id}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    postAudienceCatalog: build.mutation<void, PostAudienceCatalog>({
      query: (data) => ({
        url: `/organizer/audience-catalogs`,
        method: 'POST',
        body: data,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    patchAudienceCatalogById: build.mutation<
      AudienceResponse,
      { data: PostAudienceCatalog; id?: number | string }
    >({
      query: ({ data, id }) => ({
        url: `/organizer/audience-catalogs/${id ? id.toString() : ''}`,
        method: 'PATCH',
        body: data,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    patchAudienceCatalogStatusById: build.mutation<
      AudienceResponse,
      { id?: number; statusId: number; transition: number }
    >({
      query: ({ id, statusId, transition }) => ({
        url: `/organizer/audience-catalogs/${id ? id.toString() : ''}/status/${statusId ? statusId.toString() : ''}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
        providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
      }),
    }),
    duplicateAudienceCatalog: build.mutation<AudienceResponse, { id: number | string }>({
      query: ({ id }) => ({
        url: `/organizer/audience-catalogs/${id}/duplicate`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG],
    }),
    patchAudienceSegmentStatusById: build.mutation<
      AudienceResponse,
      { id?: number; statusId: number; transition: number }
    >({
      query: ({ id, statusId, transition }) => ({
        url: `/segments/${id ? id.toString() : ''}/status/${statusId ? statusId.toString() : ''}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
        providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
      }),
    }),
    patchAudienceSegmentsStatus: build.mutation<
      AudienceResponse,
      {
        ids: Array<number | string>;
        statusId: number;
        transition: number;
        filters?: SegmentsFilters;
      }
    >({
      query: ({ ids, statusId, transition, filters }) => ({
        url: `/segments/status/${statusId ? statusId.toString() : ''}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: {
          transition,
          filters: {
            ids,
            ...filters,
          },
        },
      }),
    }),
    patchAudienceSegmentsKeepalive: build.mutation<
      AudienceResponse,
      {
        ids: Array<number | string>;
        keepalive: boolean;
        filters?: SegmentsFilters;
        transition?: number;
      }
    >({
      query: ({ ids, keepalive, filters, transition }) => ({
        url: `/segments/keepalive`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: {
          ...(typeof transition === 'number' ? { transition } : {}),
          keepalive,
          filters: {
            ids,
            ...filters,
          },
        },
      }),
    }),
    getOrganizerAudiencesFilterOptions: build.query<
      { data: { id: number; slug: string }[] },
      { search?: string; page?: number }
    >({
      query: ({ search, page }) => {
        const params = new URLSearchParams();
        if (search && search.length) {
          params.append('search', search);
        }

        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();
        return {
          url: `/organizer/audiences/filter${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCES],
    }),
    getOrganizerSegmentsFilterOptions: build.query<
      SegmentsFilterResponse,
      { search?: string; page?: number }
    >({
      query: ({ search, page }) => {
        const params = new URLSearchParams();
        if (search && search.length) {
          params.append('search', search);
        }

        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();
        return {
          url: `/segments/filter${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    getContactSources: build.query<ContactSourcesResponse, { search?: string; page?: number }>({
      query: ({ search, page }) => {
        const params = new URLSearchParams();
        if (search) {
          params.append('filters[search]', search);
        }
        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();
        return {
          url: `/organizer/contact-sources/filter${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      transformResponse: (response: {
        data: {
          id: string | number;
          file_name?: string;
          name?: string;
          map?: ContactSourceMapItem[];
        }[];
        meta: Meta;
      }): ContactSourcesResponse => ({
        data: response.data.map(({ id, file_name, name, map }) => ({
          id: id.toString(),
          file_name: file_name ?? name ?? id.toString(),
          map: map?.map((item) => ({
            code: normalizeMappingItem(item).code,
            name: normalizeMappingItem(item).name,
            field: normalizeMappingItem(item).field ?? '',
          })),
        })),
        meta: response.meta,
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_CONTACT_SOURCES],
    }),
    postContactSourceImport: build.mutation<
      void,
      { contactSource: string; map: ContactSourceMapItem[] }
    >({
      query: ({ contactSource, map }) => ({
        url: `/organizer/contact-sources/${contactSource}/import`,
        method: 'POST',
        body: {
          map: map.map(({ code, name, field }) => ({
            code,
            name,
            field,
          })),
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_CONTACT_SOURCES],
    }),
    getContactSourceValues: build.query<
      ContactSourceValuesResponse,
      { contactSource: string; field: string }
    >({
      query: ({ contactSource, field }) => ({
        url: `/organizer/contact-sources/${contactSource}/values?field=${encodeURIComponent(field)}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_CONTACT_SOURCES],
    }),
    getIntegrations: build.query<IntegrationsResponse, void>({
      query: () => ({
        url: `/integrations`,
        headers: {
          Accept: 'application/json',
        },
      }),
      transformResponse: (response: {
        data: {
          id: number;
          name: string;
          columns: string[] | null;
          mapping_data?: {
            contacts?: { code: string; field: string; name: string }[];
          };
          status: { name: string };
          is_connected: number;
        }[];
      }): IntegrationsResponse => ({
        data: response.data
          .filter(({ status, is_connected }) => status?.name === 'enabled' && Boolean(is_connected))
          .map(({ id, name, columns, mapping_data }) => ({
            id,
            name,
            columns: mapping_data?.contacts?.map(({ field }) => field) || columns || [],
            mapping_data: mapping_data?.contacts?.length
              ? {
                  contacts: mapping_data.contacts.map((item) => ({
                    code: normalizeMappingItem(item).code,
                    name: normalizeMappingItem(item).name,
                    field: normalizeMappingItem(item).field ?? '',
                  })),
                }
              : undefined,
          })),
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    getOrganizerIntegrationsList: build.query<OrganizerIntegrationsResponse, void>({
      query: () => ({
        url: `/integrations`,
        headers: {
          Accept: 'application/json',
        },
      }),
      transformResponse: (
        response: OrganizerIntegrationsResponse,
      ): OrganizerIntegrationsResponse => ({
        data: response.data.map((integration) => ({
          ...integration,
          mapping_data: integration.mapping_data?.contacts?.length
            ? {
                contacts: integration.mapping_data.contacts.map((item) => ({
                  code: normalizeMappingItem(item).code,
                  name: normalizeMappingItem(item).name,
                  field: normalizeMappingItem(item).field ?? '',
                })),
              }
            : integration.mapping_data,
        })),
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    getIntegrationPlatforms: build.query<IntegrationPlatformsResponse, void>({
      query: () => ({
        url: `/integrations/platforms`,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    postIntegration: build.mutation<OrganizerIntegrationItem, CreateIntegrationRequest>({
      query: (payload) => ({
        url: `/integrations`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    getIntegrationMapping: build.query<
      IntegrationMappingResponse,
      { integration: number | string }
    >({
      query: ({ integration }) => ({
        url: `/integrations/${integration}/mapping`,
        headers: {
          Accept: 'application/json',
        },
      }),
      transformResponse: (response: IntegrationMappingResponse): IntegrationMappingResponse => ({
        data: {
          ...response.data,
          map: response.data.map?.map((item) => ({
            code: normalizeMappingItem(item).code,
            name: normalizeMappingItem(item).name,
            field: normalizeMappingItem(item).field,
          })),
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    patchIntegration: build.mutation<
      OrganizerIntegrationItem,
      { integration: number | string; payload: UpdateIntegrationRequest }
    >({
      query: ({ integration, payload }) => ({
        url: `/integrations/${integration}`,
        method: 'PATCH',
        body: {
          ...payload,
          mapping_data: payload.mapping_data?.contacts?.length
            ? {
                contacts: payload.mapping_data.contacts.map(({ code, name, field }) => ({
                  code,
                  name,
                  field,
                })),
              }
            : payload.mapping_data,
        },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    getIntegrationValues: build.query<
      IntegrationValuesResponse,
      { integration: string; column: string }
    >({
      query: ({ integration, column }) => ({
        url: `/integrations/${integration}/values?field=${encodeURIComponent(column)}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_INTEGRATIONS],
    }),
    getIntegrationHubspotLists: build.query<
      HubspotListsResponse,
      { integration: number | string; page?: number; search?: string }
    >({
      query: ({ integration, page, search }) => {
        const params = new URLSearchParams();
        if (page) {
          params.append('page', page.toString());
        }
        if (search) {
          params.append('search', search);
        }
        const queryString = params.toString();
        return {
          url: `/integrations/${integration}/hubspot/lists${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
    }),
    getIntegrationHubspotListFields: build.query<
      HubspotListFieldsResponse,
      { integration: number | string; list_id: string }
    >({
      query: ({ integration, list_id }) => ({
        url: `/integrations/${integration}/hubspot/lists/fields?list_id=${encodeURIComponent(
          list_id,
        )}`,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    getIntegrationHubspotListPreview: build.query<
      HubspotListPreviewQueuedResponse,
      {
        integration: number | string;
        list_id: string;
        mapping: {
          contacts?: HubspotListPreviewMappingItem[];
          companies?: HubspotListPreviewMappingItem[];
        };
      }
    >({
      query: ({ integration, list_id, mapping }) => {
        const params = new URLSearchParams();
        params.append('list_id', list_id);
        mapping.contacts?.forEach((item, index) => {
          if (item.code) params.append(`mapping[contacts][${index}][code]`, item.code);
          if (item.field) params.append(`mapping[contacts][${index}][field]`, item.field);
          if (item.name) params.append(`mapping[contacts][${index}][name]`, item.name);
        });
        mapping.companies?.forEach((item, index) => {
          if (item.code) params.append(`mapping[companies][${index}][code]`, item.code);
          if (item.field) params.append(`mapping[companies][${index}][field]`, item.field);
          if (item.name) params.append(`mapping[companies][${index}][name]`, item.name);
        });
        return {
          url: `/integrations/${integration}/hubspot/lists/preview?${params.toString()}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
    }),
    postIntegrationHubspotListEnableImport: build.mutation<
      HubspotListEnableImportResponse,
      {
        integration: number | string;
        list_id: string;
        event_id: string | number;
        mapping: {
          contacts?: HubspotListPreviewMappingItem[];
          companies?: HubspotListPreviewMappingItem[];
        };
      }
    >({
      query: ({ integration, list_id, event_id, mapping }) => ({
        url: `/integrations/${integration}/hubspot/lists/enable-import`,
        method: 'POST',
        body: {
          list_id,
          event_id,
          mapping,
        },
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    getIntegrationLists: build.query<HubspotListImportsResponse, { eventId: string | number }>({
      query: ({ eventId }) => {
        const params = new URLSearchParams();
        params.append('filters[event_ids][]', eventId.toString());
        return {
          url: `/integrations/lists?${params.toString()}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
    }),
    patchIntegrationListStatus: build.mutation<
      { data: HubspotListImportItem },
      { integrationListId: number; statusId: number; transition: number }
    >({
      query: ({ integrationListId, statusId, transition }) => ({
        url: `/integrations/lists/${integrationListId}/status/${statusId}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
      }),
    }),
    patchIntegrationList: build.mutation<
      { data: HubspotListImportItem },
      {
        integrationListId: number;
        mapping: {
          contacts?: HubspotListPreviewMappingItem[];
          companies?: HubspotListPreviewMappingItem[];
        };
      }
    >({
      query: ({ integrationListId, mapping }) => ({
        url: `/integrations/lists/${integrationListId}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { mapping },
      }),
    }),
    postIntegrationHubspotListConnect: build.mutation<
      HubspotListConnectResponse,
      { integration: number | string; list_id: string }
    >({
      query: ({ integration, list_id }) => ({
        url: `/integrations/${integration}/hubspot/lists/connect`,
        method: 'POST',
        body: { list_id },
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    getOrganizerTrackHosts: build.query<TrackHostsResponse, { page: number }>({
      query: ({ page }) => {
        return {
          url: `/organizer/track-hosts/filter?page=${page}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    getOrganizerUnsegmentsCount: build.query<{ data: { count: number } }, void>({
      query: () => {
        return {
          url: `/organizer/audiences/unsegments-count`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    getOrganizerTrackHostsActions: build.query<
      TrackHostsActionsResponse,
      { hosts: string[]; page?: number; is_form?: number }
    >({
      query: ({ hosts, page, is_form }) => {
        const params = new URLSearchParams();
        const hostsQueryString = getQueryStringFromParams(hosts, 'hosts');

        if (page) {
          params.append('page', page.toString());
        }
        const searchQueryString = params.toString();
        const queryString = [
          hostsQueryString,
          searchQueryString,
          is_form ? `filters[is_form]=${is_form}` : '',
        ]
          .filter(Boolean)
          .join('&');
        return {
          url: `/organizer/track-hosts/actions${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_AUDIENCE_SEGMENTS],
    }),
    postSetupAudienceCatalog: build.mutation<void, { eventId: string; audienceCatalogId: string }>({
      query: ({ eventId, audienceCatalogId }) => ({
        url: `/organizer/events/${eventId}/setup/audience-catalog/${audienceCatalogId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_AUDIENCE_CATALOG },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
  }),
});

export const {
  useGetOrganizerSegmentsQuery,
  useGetOrganizerAudiencesQuery,
  useGetOrganizerAudienceByIdQuery,
  usePostAudienceMutation,
  usePatchAudienceByIdMutation,
  usePatchAudienceStatusByIdMutation,
  useGetOrganizerAudienceCatalogsQuery,
  useGetOrganizerAudienceCatalogByIdQuery,
  useGetOrganizerSegmentByIdQuery,
  usePostAudienceCatalogMutation,
  usePatchAudienceCatalogByIdMutation,
  usePostSegmentMutation,
  usePatchSegmentByIdMutation,
  usePatchAudienceCatalogStatusByIdMutation,
  useDuplicateAudienceCatalogMutation,
  usePatchAudienceSegmentStatusByIdMutation,
  usePatchAudienceSegmentsStatusMutation,
  usePatchAudienceSegmentsKeepaliveMutation,
  useGetOrganizerAudiencesFilterOptionsQuery,
  useGetOrganizerSegmentsFilterOptionsQuery,
  useGetContactSourcesQuery,
  useLazyGetContactSourcesQuery,
  usePostContactSourceImportMutation,
  useGetContactSourceValuesQuery,
  useGetIntegrationsQuery,
  useGetOrganizerIntegrationsListQuery,
  useGetIntegrationPlatformsQuery,
  usePostIntegrationMutation,
  useGetIntegrationMappingQuery,
  useLazyGetIntegrationMappingQuery,
  usePatchIntegrationMutation,
  useGetIntegrationValuesQuery,
  useGetIntegrationHubspotListsQuery,
  useGetIntegrationHubspotListFieldsQuery,
  useGetIntegrationHubspotListPreviewQuery,
  useLazyGetIntegrationHubspotListPreviewQuery,
  usePostIntegrationHubspotListEnableImportMutation,
  useGetIntegrationListsQuery,
  usePatchIntegrationListStatusMutation,
  usePatchIntegrationListMutation,
  usePostIntegrationHubspotListConnectMutation,
  useGetOrganizerTrackHostsQuery,
  useGetOrganizerTrackHostsActionsQuery,
  useLazyGetOrganizerTrackHostsActionsQuery,
  useGetOrganizerUnsegmentsCountQuery,
} = organizerAudiencesApi;
